import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { motion } from "framer-motion";
import {
	memo,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../../../../components/CustomButton/CustomButton.js";
import FiltroV2 from "../../../../../components/FiltroV2/FiltroV2.js";
import NavbarV3 from "../../../../../components/NavbarV3/NavbarV3.tsx";
import ToastErro from "../../../../../components/Toast/ToastErro/ToastErro.tsx";
import MultiplosFiltrosRedux from "../../../../../contexts/MultiplosFiltros.js";
import { useProfile } from "../../../../../contexts/ProfileContext.tsx";
import RastrearRedux from "../../../../../contexts/Rastreio.js";
import MotionSemAutorizacao from "../../../../../core/components/MotionSemAutorizacao.tsx";
import { formatarDataISO } from "../../../../../core/utils/UtilityDates.ts";
import caminhaoPinCarregando from "../../../../../images/cam-greem-removebg-preview.png";
import {
	getEmbarqueMesoRegiaSearch,
	getEmbarquePorRotaMesoRegiaSearch,
} from "../../../../../services/ModuloGerenciador/Search.js";
import EventosCard from "../components/EventosCard.tsx";
import "../components/LoadingModal/LoadingModal.css";
import LoadingModal from "../components/LoadingModal/LoadingModal.tsx";
import RastreioCard from "../components/RastreioCard.tsx";
import "./MapaMeso.css";

const itensFiltro = [
	{ nomeColuna: "Placa Cavalo", field: "placaCavalo", filtro: "true" },
	{ nomeColuna: "Placa Carreta", field: "placaCarreta", filtro: "true" },
	{
		nomeColuna: "Placa Segunda Carreta",
		field: "placaCarreta2",
		filtro: "true",
	},
	{ nomeColuna: "Proprietario", field: "proprietario" },
	{ nomeColuna: "Motorista", field: "veiculoMotorista" },
	{ nomeColuna: "Motorista2", field: "motorista2" },
];

function MapaMeso() {
	const navigate = useNavigate();

	const voltar = () => {
		navigate("/mesorregiao");
		setDadosRastrearRedux({});
	};

	const { dadosRastrearRedux, setDadosRastrearRedux } =
		useContext(RastrearRedux);

	const { profile, hasRegra, handleSetContextoEmbarque } = useProfile();

	const { multiplosFiltrosRedux } = useContext(MultiplosFiltrosRedux);

	const mapRef = useRef(null);

	const [loading, setLoading] = useState(true);

	const [isSearching, setIsSearching] = useState(true);

	const [semAutorizacao, setSemAutorizacao] = useState(false);

	const [openToastErro, setOpenToastErro] = useState(false);

	const [dadosEventos] = useState([]);

	const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);

	const [mesoRotaNome, setMesoRotaNome] = useState(null);

	const [mesos, setMesos] = useState([]);

	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
	});

	const [isMapLoaded, setIsMapLoaded] = useState(false);

	const [polygon] = useState({ paths: [] });

	const [drawnPolygon, setDrawnPolygon] = useState(null);

	const [textoErro, setTextoErro] = useState("");

	const [valuesPesquisa, setValuesPesquisa] = useState({
		item: undefined,
		pesquisa: undefined,
		embarqueInicio: undefined,
		embarqueFinal: undefined,
	});

	const mapCenter = useMemo(
		() => ({
			lat: -13.352436164089657,
			lng: -49.59815951547697,
		}),
		[]
	);

	const buscarMapaMeso = useCallback(
		async ({ pesquisaFormatada, dataInicio, dataFim, contexto }) => {
			setIsSearching(true);
			try {
				const response = await getEmbarqueMesoRegiaSearch({
					token: profile?.token,
					pesquisa: pesquisaFormatada,
					contexto: contexto,
					dataInicio: dataInicio,
					dataFim: dataFim,
					ordenarPor: undefined,
					cancelado: false,
				});
				setVeiculoSelecionado(null);
				setMesos(response.data.agrupado);
			} catch (error) {
				if (error.response?.data?.length > 200) {
					setTextoErro(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
					);
				} else {
					setTextoErro(error.response.data);
				}
				setOpenToastErro(true);
			}
			setIsSearching(false);
		},
		[profile?.token]
	);

	const buscarMapaMesoInicial = useCallback(async () => {
		if (!profile) return;
		setLoading(true);
		buscarMapaMeso({
			pesquisaFormatada: "undefined=undefined",
			dataInicio: formatarDataISO(new Date()),
			dataFim: formatarDataISO(new Date()),
			contexto: "todos",
		});
		setLoading(false);
	}, [buscarMapaMeso, valuesPesquisa, profile]);

	const buscarMapaMesoSearch = useCallback(async () => {
		if (!profile) return;
		let pesquisaFormatada = `${valuesPesquisa.item}=${valuesPesquisa.pesquisa}`;

		if (multiplosFiltrosRedux && multiplosFiltrosRedux?.length > 0) {
			for (let filtro of multiplosFiltrosRedux) {
				pesquisaFormatada += `&${filtro.field}=${filtro.valor}`;
			}
		}

		if (valuesPesquisa.embarqueInicio && valuesPesquisa.embarqueFinal) {
			buscarMapaMeso({
				pesquisaFormatada,
				dataInicio: valuesPesquisa.embarqueInicio,
				dataFim: valuesPesquisa.embarqueFinal,
				contexto: valuesPesquisa.contexto || "todos",
			});
		}
	}, [multiplosFiltrosRedux, valuesPesquisa, buscarMapaMeso, profile]);

	const buscarEmbarqueMesoIdSearch = useCallback(
		async (id) => {
			setIsSearching(true);
			let pesquisaFormatada = `${valuesPesquisa.item}=${valuesPesquisa.pesquisa}`;
			if (multiplosFiltrosRedux && multiplosFiltrosRedux?.length > 0) {
				for (let filtro of multiplosFiltrosRedux) {
					pesquisaFormatada += `&${filtro.field}=${filtro.valor}`;
				}
			}
			await getEmbarquePorRotaMesoRegiaSearch(
				profile?.token,
				pesquisaFormatada,
				id,
				valuesPesquisa.contexto,
				valuesPesquisa.embarqueInicio,
				valuesPesquisa.embarqueFinal,
				undefined,
				false
			)
				.then((result) => {
					setVeiculoSelecionado(result.data);
				})
				.catch((error) => {
					if (error.response?.data?.length > 200) {
						setTextoErro(
							"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
						);
					} else {
						setTextoErro(error.response.data);
					}
					setOpenToastErro(true);
				});
			setIsSearching(false);
		},
		[profile?.token, multiplosFiltrosRedux, valuesPesquisa]
	);

	const handleMarkerClick = useCallback(
		(veiculo) => {
			setMesoRotaNome(veiculo.nomeMesorregiao);
			buscarEmbarqueMesoIdSearch(veiculo.id);
		},
		[buscarEmbarqueMesoIdSearch]
	);

	const handleCloseToastErro = useCallback(() => {
		setOpenToastErro(false);
	}, []);

	const navegarEdicaoEmbarque = useCallback(
		(veiculo) => {
			handleSetContextoEmbarque(veiculo.contexto);
			navigate(`/viagens/${veiculo.contexto}/cadastro/${veiculo.idEmbarque}`);
		},
		[navigate, handleSetContextoEmbarque]
	);

	const handleMapLoaded = (map) => {
		mapRef.current = map;
		setIsMapLoaded(true);
	};

	useEffect(() => {
		const applyCustomStyles = () => {
			const cameraControl = document.querySelector(
				"gmp-internal-camera-control"
			);
			if (cameraControl) {
				const parentElement = cameraControl.parentElement;
				if (parentElement) {
					parentElement.style.right = "100px";
				}
				const menuElement = cameraControl.querySelector("menu");
				if (menuElement) {
					menuElement.style.width = "315px";
					menuElement.style.height = "175px";
					menuElement.style.top = "-80px";
				}
			}
		};

		const handleFullscreen = () => {
			setTimeout(() => {
				applyCustomStyles();
			}, 500);
		};

		const monitorFullscreen = () => {
			const fullscreenButton = document.querySelector(".gm-fullscreen-control");
			if (fullscreenButton) {
				fullscreenButton.addEventListener("click", handleFullscreen);
				document.addEventListener("keydown", (event) => {
					if (event.key === "Escape") {
						handleFullscreen();
					}
				});
			}
		};

		let observer;

		if (isMapLoaded) {
			applyCustomStyles();
			monitorFullscreen();

			observer = new MutationObserver(() => {
				applyCustomStyles();
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});
		}

		document.addEventListener("fullscreenchange", handleFullscreen);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreen);
			const fullscreenButton = document.querySelector(".gm-fullscreen-control");
			if (fullscreenButton) {
				fullscreenButton.removeEventListener("click", handleFullscreen);
			}
			if (observer) {
				observer.disconnect();
			}
		};
	}, [isMapLoaded]);

	const limpar = useCallback(() => {
		buscarMapaMesoInicial();
	}, [buscarMapaMesoInicial]);

	useEffect(() => {
		if (profile?.token) {
			setSemAutorizacao(!hasRegra("Mesorregião"));
		}
	}, [profile, hasRegra]);

	useEffect(() => {
		if (
			valuesPesquisa.embarqueInicio &&
			valuesPesquisa.embarqueFinal &&
			profile?.token &&
			mesos.length === 0
		) {
			buscarMapaMesoInicial();
		}
	}, [
		valuesPesquisa.embarqueInicio,
		valuesPesquisa.embarqueFinal,
		profile,
		buscarMapaMesoInicial,
	]);

	useEffect(() => {
		if (isMapLoaded && polygon.paths?.length > 0 && window.google) {
			if (drawnPolygon) {
				drawnPolygon.setMap(null);
			}

			const newPolygon = new window.google.maps.Polygon({
				paths: polygon.paths,
				strokeColor: "#FF0000",
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: "#FF0000",
				fillOpacity: 0.35,
			});

			newPolygon.setMap(mapRef.current);
			setDrawnPolygon(newPolygon);
		}
	}, [drawnPolygon, polygon, isMapLoaded]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	if (loadError) {
		return (
			<>
				<NavbarV3 />
				<div>Erro ao carregar o mapa</div>
			</>
		);
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			style={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				height: "100%",
			}}
		>
			<NavbarV3 />
			<div className="principal-mapa">
				<div className="voltar-card-veiculos">
					<FiltroV2
						classe="div-filtrar-embarque-v2"
						classOption="select-options-cliente"
						itensFiltro={itensFiltro}
						valuesPesquisa={valuesPesquisa}
						setValuesPesquisa={setValuesPesquisa}
						buscarSearch={buscarMapaMesoSearch}
						limpar={limpar}
					/>
					<CustomButton
						style={{ width: "300px" }}
						classCss="custom-button-meso"
						titulo="Mesorregião"
						cor="#0946B8"
						onClick={() => voltar()}
					/>
				</div>
				{dadosRastrearRedux.endereco && (
					<>{!loading && <EventosCard dadosEventos={dadosEventos} />}</>
				)}
				{!dadosRastrearRedux.endereco && veiculoSelecionado && (
					<RastreioCard
						veiculoSelecionado={veiculoSelecionado}
						onClose={() => setVeiculoSelecionado(null)}
						mesoRotaNome={mesoRotaNome}
						navegarEdicaoEmbarque={navegarEdicaoEmbarque}
					/>
				)}
				{isLoaded && (
					<GoogleMap
						useLoadScript
						mapContainerStyle={{
							width: "100%",
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
						center={mapCenter}
						zoom={5}
						onLoad={handleMapLoaded}
					>
						{isMapLoaded &&
							mesos !== null &&
							mesos.length > 0 &&
							mesos.map((localizador, index) => {
								return (
									<Marker
										key={index}
										position={{
											lat: localizador.latCentro,
											lng: localizador.longCentro,
										}}
										icon={{
											url: caminhaoPinCarregando,
											scaledSize: new window.google.maps.Size(50, 50),
											labelOrigin: new window.google.maps.Point(25, 1),
										}}
										label={{
											text: `${localizador.embarques.length}`,
											color: "black",
											fontSize: "18px",
											fontWeight: "bold",
										}}
										onClick={() => handleMarkerClick(localizador)}
									/>
								);
							})}
					</GoogleMap>
				)}
			</div>
			<div>
				<ToastErro
					texto={textoErro}
					open={openToastErro}
					onClose={handleCloseToastErro}
				/>
			</div>

			<LoadingModal isOpen={isSearching} />
		</motion.div>
	);
}

export default memo(MapaMeso);
